import type MapboxDraw from '@mapbox/mapbox-gl-draw';

type DrawMode = ReturnType<MapboxDraw['getMode']>;

function restartDrawMode(draw: MapboxDraw, lineMode: boolean) {
  const mode = lineMode ? 'draw_line_string' : 'draw_polygon';
  draw.changeMode(mode as 'simple_select');
}

function activeDrawFeature(draw: MapboxDraw): GeoJSON.Feature | null {
  const selectedIds = draw.getSelectedIds();
  if (selectedIds.length > 0) {
    const selected = draw.get(selectedIds[0]);
    if (selected) return selected;
  }
  const all = draw.getAll();
  return all.features.length > 0 ? all.features[all.features.length - 1] : null;
}

function commitFeature(
  draw: MapboxDraw,
  feature: GeoJSON.Feature,
  mode: DrawMode,
): void {
  const id = String(feature.id);
  // Leave in-progress sketch modes before set() or coordinates won't stick.
  if (mode === 'draw_line_string' || mode === 'draw_polygon' || mode === 'direct_select') {
    draw.changeMode('simple_select');
  }
  draw.set({
    type: 'FeatureCollection',
    features: [feature],
  });
  draw.changeMode('simple_select', { featureIds: [id] });
}

/** Remove the first vertex on a line or open polygon ring. */
export function undoDrawFirstVertex(draw: MapboxDraw, lineMode: boolean): boolean {
  const mode = draw.getMode() as DrawMode;
  const feature = activeDrawFeature(draw);
  if (!feature) return false;

  const geom = feature.geometry;

  if (geom.type === 'LineString') {
    const coords = [...geom.coordinates] as [number, number][];
    if (coords.length <= 1) {
      draw.deleteAll();
      restartDrawMode(draw, lineMode);
      return false;
    }
    coords.shift();
    commitFeature(
      draw,
      {
        type: 'Feature',
        id: feature.id,
        properties: feature.properties ?? {},
        geometry: { type: 'LineString', coordinates: coords },
      },
      mode,
    );
    return true;
  }

  if (geom.type === 'Polygon') {
    const ring = [...(geom.coordinates[0] as [number, number][])];
    const open =
      ring.length > 1
      && ring[0][0] === ring[ring.length - 1][0]
      && ring[0][1] === ring[ring.length - 1][1]
        ? ring.slice(0, -1)
        : ring;

    if (open.length <= 1) {
      draw.deleteAll();
      restartDrawMode(draw, lineMode);
      return false;
    }

    open.shift();
    const closed = open.length >= 3 ? [...open, open[0]] : open;
    commitFeature(
      draw,
      {
        type: 'Feature',
        id: feature.id,
        properties: feature.properties ?? {},
        geometry: { type: 'Polygon', coordinates: [closed] },
      },
      mode,
    );
    return open.length >= 3;
  }

  return false;
}

/** Remove the last placed vertex while drawing, or the last vertex on a finished shape. */
export function undoDrawLastVertex(draw: MapboxDraw, lineMode: boolean): boolean {
  const mode = draw.getMode() as DrawMode;
  const feature = activeDrawFeature(draw);
  if (!feature) return false;

  const geom = feature.geometry;

  if (geom.type === 'LineString') {
    const coords = [...geom.coordinates] as [number, number][];
    if (coords.length <= 1) {
      draw.deleteAll();
      restartDrawMode(draw, lineMode);
      return false;
    }
    coords.pop();
    commitFeature(
      draw,
      {
        type: 'Feature',
        id: feature.id,
        properties: feature.properties ?? {},
        geometry: { type: 'LineString', coordinates: coords },
      },
      mode,
    );
    return true;
  }

  if (geom.type === 'Polygon') {
    const ring = [...(geom.coordinates[0] as [number, number][])];
    const open =
      ring.length > 1
      && ring[0][0] === ring[ring.length - 1][0]
      && ring[0][1] === ring[ring.length - 1][1]
        ? ring.slice(0, -1)
        : ring;

    if (open.length <= 1) {
      draw.deleteAll();
      restartDrawMode(draw, lineMode);
      return false;
    }

    open.pop();
    const closed = open.length >= 3 ? [...open, open[0]] : open;
    commitFeature(
      draw,
      {
        type: 'Feature',
        id: feature.id,
        properties: feature.properties ?? {},
        geometry: { type: 'Polygon', coordinates: [closed] },
      },
      mode,
    );
    return open.length >= 3;
  }

  return draw.getAll().features.length > 0;
}

/** Clear drawing or delete the selected vertex/feature. Returns false when map is empty afterward. */
export function enterDrawDirectSelect(draw: MapboxDraw): boolean {
  const feature = activeDrawFeature(draw);
  if (!feature?.id) return false;
  draw.changeMode('direct_select', { featureId: String(feature.id) });
  return true;
}

export function replaceDrawGeometry(
  draw: MapboxDraw,
  geometry: GeoJSON.LineString | GeoJSON.Polygon,
  _lineMode: boolean,
): boolean {
  const feature = activeDrawFeature(draw);
  const id = feature?.id ?? 'drawn-feature';
  commitFeature(
    draw,
    {
      type: 'Feature',
      id,
      properties: feature?.properties ?? {},
      geometry,
    },
    draw.getMode() as DrawMode,
  );
  return true;
}

export function trashDraw(draw: MapboxDraw, lineMode: boolean): boolean {
  const mode = draw.getMode() as DrawMode;

  if (mode === 'direct_select') {
    draw.trash();
    if (draw.getAll().features.length === 0) {
      restartDrawMode(draw, lineMode);
      return false;
    }
    return true;
  }

  if (mode === 'simple_select' && draw.getSelectedIds().length > 0) {
    draw.trash();
    if (draw.getAll().features.length === 0) {
      restartDrawMode(draw, lineMode);
      return false;
    }
    return true;
  }

  draw.deleteAll();
  restartDrawMode(draw, lineMode);
  return false;
}
